import { useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router'
import { Check, Heart } from 'lucide-react'
import {
  Combobox,
  ComboboxContent,
  ComboboxTrigger,
} from 'tailwind-v4/components/popover-combobox'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from 'tailwind-v4/components/command'
import { SwitcherChevron } from '@/components/nav-v4/switcher-chevron'
import { useFeatureFlags } from '@/hooks/use-feature-flags'
import loyaltyApi from '@/state/loyalty/loyalty.slice'
import { parseSchemeId } from '../old-nav-config'

/**
 * Loyalty scheme picker for the top bar — the venue-switcher pattern applied to
 * loyalty schemes (org-based, with venues included). Shown only in the loyalty
 * section; picking a scheme keeps you on the same service sub-page.
 *
 * Built on the SAME primitives as `VenueSwitcher` (Combobox + Command +
 * SwitcherChevron) rather than a hand-styled `JosefPopover`, because the two
 * sit side by side in the old top bar and any divergence reads as a bug: the
 * old build had a bordered white pill with a plain chevron next to the
 * switcher's borderless trigger.
 */
export function OldSchemePicker() {
  const { org_id } = useParams<{ org_id: string }>()
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const { newUi } = useFeatureFlags()

  const { data: schemes = [] } = loyaltyApi.useGetLoyaltySchemesQuery(
    { orgId: org_id ?? '' },
    { skip: !org_id }
  )

  const schemeId = parseSchemeId(pathname)
  const current = useMemo(
    () => (schemes as any[]).find((s) => s?.id === schemeId),
    [schemes, schemeId]
  )
  const filtered = useMemo(() => {
    if (!search) return schemes as any[]
    const q = search.toLowerCase()
    return (schemes as any[]).filter((s) =>
      (s?.name ?? '').toLowerCase().includes(q)
    )
  }, [schemes, search])

  const label = current?.name ?? 'All schemes'

  function goToScheme(id: string) {
    const segs = pathname.split('/').filter(Boolean)
    const i = segs.indexOf('loyalty')
    if (i >= 0) {
      const next = [...segs]
      if (schemeId) {
        next[i + 1] = id
      } else {
        next.splice(i + 1, 0, id)
      }
      navigate(`/${next.join('/')}`)
    } else {
      navigate(`/${org_id}/loyalty/${id}`)
    }
    setOpen(false)
  }

  function goToAll() {
    const segs = pathname.split('/').filter(Boolean)
    const i = segs.indexOf('loyalty')
    if (i >= 0 && schemeId) {
      // strip the scheme id (and any service after it) → back to the list
      navigate(`/${segs.slice(0, i + 1).join('/')}`)
    } else {
      navigate(`/${org_id}/loyalty`)
    }
    setOpen(false)
  }

  return (
    <Combobox open={open} onOpenChange={setOpen}>
      <ComboboxTrigger className="font-medium">
        <span className="min-w-0 max-w-[160px] truncate">{label}</span>
        <SwitcherChevron />
      </ComboboxTrigger>
      <ComboboxContent className={newUi.enabled ? undefined : 'old-ui-nav'}>
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Find scheme..."
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            <CommandEmpty>No scheme found.</CommandEmpty>
            <CommandGroup>
              <CommandItem value="__all__" onSelect={goToAll}>
                <Heart className="size-4" />
                <span>All schemes</span>
                {!schemeId ? <Check className="ml-auto size-4" /> : null}
              </CommandItem>
            </CommandGroup>
            {filtered.length > 0 ? (
              <CommandGroup>
                {filtered.map((s) => {
                  const id = s?.id as string
                  const name = (s?.name as string) ?? 'Unnamed Scheme'
                  return (
                    <CommandItem
                      key={id}
                      value={id}
                      onSelect={() => {
                        goToScheme(id)
                      }}
                    >
                      <SchemeInitial name={name} />
                      <span className="truncate">{name}</span>
                      {id === schemeId ? (
                        <Check className="ml-auto size-4" />
                      ) : null}
                    </CommandItem>
                  )
                })}
              </CommandGroup>
            ) : null}
          </CommandList>
        </Command>
      </ComboboxContent>
    </Combobox>
  )
}

/** Mirrors `VenueInitial` in the venue switcher so both lists read alike. */
function SchemeInitial({ name }: { name: string }) {
  const initial = (name?.[0] ?? '?').toUpperCase()
  return (
    <span
      aria-hidden
      className="flex size-5 shrink-0 items-center justify-center rounded-sm bg-[var(--accent)] text-[10px] font-semibold text-[var(--accent-foreground)]"
    >
      {initial}
    </span>
  )
}
